import { OmniusCargoColumnType } from "../griddle/CargoColumns";
import { ColumnConfig, parseNumber, wafflesKeyColumnName } from "./exports";

enum WafflesFilterType {
  string,
  number,
  boolean,
  dateTime,
}

const mapOmniusCargoTypeToFilterType = (type: OmniusCargoColumnType): WafflesFilterType => {
  switch (type) {
    case OmniusCargoColumnType.bool:
      return WafflesFilterType.boolean;
    case OmniusCargoColumnType.decimal:
    case OmniusCargoColumnType.int:
      return WafflesFilterType.number;
    case OmniusCargoColumnType.dateTime:
      return WafflesFilterType.dateTime;
    case OmniusCargoColumnType.string:
    default:
      return WafflesFilterType.string;
  }
};

// These aren't actually used (obviously), but I'd like to keep them as documentation
//type StringQualifiers = "equal to" | "contains" | "does not contain";

//type NumberQualifiers = "greater than" | "equal to" | "less than";

enum WafflesFilterLogic {
  And = "and",
  Or = "or",
}

interface ColumnFilterValue {
  operator?: string;
  operatorDisplayValue?: string;
  value: string | boolean;
}

interface ColumnFilter {
  displayName: string;
  key: string;
  type: WafflesFilterType;
  value: ColumnFilterValue;
}

interface WafflesFilterGroup {
  cols: ColumnFilter[];
  logicInternal: WafflesFilterLogic;
  logicPrevGroup: WafflesFilterLogic;
}

const wafflesNumberFilter = (dataString: string, filterString: string): boolean => {
  return parseFloat(dataString.replace(/,/g, "")) > parseFloat(filterString.replace(/,/g, ""));
};

const wafflesStringFilter = (dataString: string, filterString: string): boolean => {
  return dataString.toLowerCase().includes(filterString.toLowerCase());
};

function genericFilter<T>(row: T, colKey: string, columnFilter: ColumnFilter): boolean {
  const rowString = row[colKey as keyof T] as unknown as string;
  switch (columnFilter.type) {
    case WafflesFilterType.string:
      // type: string
      switch (columnFilter.value.operator) {
        case "equal to":
          return rowString === columnFilter.value.value as string;
        case "contains":
          return wafflesStringFilter(rowString, columnFilter.value.value as string);
        case "does not contain":
          return !wafflesStringFilter(rowString, columnFilter.value.value as string);
      }
    case WafflesFilterType.number:
      // type: number
      switch (columnFilter.value.operator) {
        case "greater than":
          // removing the commas here only works with the US number system, but fortunately, I'm pretty sure that's all we're dealing with
          return parseNumber(rowString) > parseNumber((columnFilter.value.value as string));
        case "equal to":
          return parseNumber(rowString) === parseNumber((columnFilter.value.value as string));
        case "less than":
          return parseNumber(rowString) < parseNumber((columnFilter.value.value as string));
      }
    case WafflesFilterType.boolean:
      // type: boolean
      // in this case, we only care about whether the element exists
      return !!rowString === columnFilter.value.value;
    case WafflesFilterType.dateTime:
      // type: Date
      // Dates are complicated. JS stores a Date as a number (ms since epoch) with no timezone information and applies the transformation when called.
      // The 00:00:00 sets it to midnight and tells it to not make any guesses about timezone based on the client's machine
      // And valueOf actually exposes the number for comparison instead of the Date object
      switch (columnFilter.value.operator) {
        case "greater than": // after
          return new Date(`${rowString} 00:00:00`).valueOf() > new Date(`${columnFilter.value.value as string} 00:00:00`).valueOf();
        case "equal to":
          return new Date(`${rowString} 00:00:00`).valueOf() === new Date(`${columnFilter.value.value as string} 00:00:00`).valueOf();
        case "less than": // before
          return new Date(`${rowString} 00:00:00`).valueOf() < new Date(`${columnFilter.value.value as string} 00:00:00`).valueOf();
      }
    default:
      console.warn(`Could not filter ${colKey}`);
      return;
  }
}

// I feel like there's a better way to handle this,
// iterating over the records and using Array.some and .every for OR and AND,
// but I can't wrap my head around doing that succinctly for multiple groups of filters
function wafflesDefaultFiltering<T extends { [wafflesKeyColumnName]: string }>(dataToFilter: T[], filters: WafflesFilterGroup[], typeRules: (row: T, columnFilter: ColumnFilter) => boolean): T[] {
  if (dataToFilter.length === 0 || filters.length === 0) {
    return dataToFilter;
  }
  let result: T[] = [];

  const applyLogic = (logic: WafflesFilterLogic, parentGroup: T[], childGroup: T[]) => {
    const cloneKeys = childGroup.map((cf) => cf[wafflesKeyColumnName]);
    if (logic === WafflesFilterLogic.And) {
      parentGroup = parentGroup.filter((x) => cloneKeys.includes(x[wafflesKeyColumnName]));
    } else {
      // combine the two and dedupe based on the key
      parentGroup = parentGroup.concat(childGroup);
      parentGroup = parentGroup.filter((pg, index, self) => {
        return self.findIndex((s) => s[wafflesKeyColumnName] === pg[wafflesKeyColumnName]) === index;
      });
    }
    return parentGroup;
  };

  let colGroupFiltered: T[] = [];
  filters.forEach((filterGroup, groupIndex) => {
    colGroupFiltered = [];
    filterGroup.cols.forEach((col, colIndex) => {
      const colFiltered: T[] = [];
      dataToFilter.forEach((dc) => {
        if (typeRules(dc, col)) {
          colFiltered.push(dc);
        }
      });
      if (colIndex === 0) {
        colGroupFiltered = [...colFiltered];
      } else {
        colGroupFiltered = applyLogic(filterGroup.logicInternal, colGroupFiltered, colFiltered);
      }
    });
    if (groupIndex === 0) {
      result = [...colGroupFiltered];
    } else {
      result = applyLogic(filterGroup.logicPrevGroup, result, colGroupFiltered);
    }
  });
  return result;
}

interface APIFilterCriteria {
  field: string;
  groupWithPrevious: boolean;
  needsBrackets?: boolean;
  needsQuotes?: boolean;
  nextConnector: number;
  opCode: number;
  values: string[] | boolean[];
}

const wafflesLogicToOpTypeMap = new Map([[WafflesFilterLogic.And, 1], [WafflesFilterLogic.Or, 2]]);
const opTypeToWafflesLogicMap = new Map([[1, WafflesFilterLogic.And], [2, WafflesFilterLogic.Or]]);

const wafflesOperatorToBasicOpTypeMap = new Map([
  ["contains", 14],
  ["does not contain", 15],
  ["greater than", 5],
  ["equal to", 1],
  ["less than", 3],
  ["yes", 10],
  ["no", 9],
]);
const basicOpTypeToWafflesOperatorMap = new Map([
  [14, "contains"],
  [15, "does not contain"],
  [5, "greater than"],
  [1, "equal to"],
  [3, "less than"],
  [10, "yes"],
  [9, "no"],
]);

const translateWafflesFiltersToApiFilterCriteria = (filters: WafflesFilterGroup[]): APIFilterCriteria[] => {
  const apiFilters: APIFilterCriteria[] = [];
  filters.forEach((group, groupIndex, groupArr) => {
    group.cols.forEach((col, colIndex, colArr) => {
      const nextLogic = colIndex < colArr.length - 1 ?
        group.logicInternal :
        groupIndex < groupArr.length - 1 ?
          groupArr[groupIndex + 1].logicPrevGroup :
          null;
      apiFilters.push({
        field: col.key,
        groupWithPrevious: colIndex > 0,
        needsBrackets: false,
        needsQuotes: false,
        // If it's still in the same group, use the group's internal logic operator
        // If it's the end of the current group, and not the last group, use the next group's logicPrevGroup
        // If it's the last group, it doesn't matter
        nextConnector: wafflesLogicToOpTypeMap.get(nextLogic), // needs to match LogicalOperatorTypeEnum
        opCode: wafflesOperatorToBasicOpTypeMap.get(col.value.operator), // needs to match BasicOperatorTypeEnum
        values: [col.value.value] as string[],
      });
    });
  });

  return apiFilters;
};

const translateAPIFilterCriteriaToWafflesFilters = (apiFilters: APIFilterCriteria[], columns: ColumnConfig): WafflesFilterGroup[] => {
  const wafflesFilters: WafflesFilterGroup[] = [];
  apiFilters.forEach((item, index, apiArr) => {
    const col = columns[item.field];
    let filterGroup: WafflesFilterGroup;
    if (index === 0 || !item.groupWithPrevious) {
      // If the next item is grouped with this one, we can use the nextConnector as an internal value
      const logicInternal = opTypeToWafflesLogicMap.get(index < apiArr.length - 1 && apiArr[index + 1].groupWithPrevious ? item.nextConnector : null);
      // Since this is the start of a new group, we can use the previous item's nextConnector for the logicPrevGroup value
      const logicPrevGroup = opTypeToWafflesLogicMap.get(index > 0 ? apiArr[index - 1].nextConnector : null);
      filterGroup = {
        cols: [],
        logicInternal: logicInternal || WafflesFilterLogic.And,
        logicPrevGroup: logicPrevGroup || WafflesFilterLogic.And,
      };
    } else {
      // If we are grouping with previous, this filter will be added onto the last group
      filterGroup = wafflesFilters.slice(-1)[0];
    }
    const newFilter: ColumnFilter = {
      displayName: col.filterText || col.displayText,
      key: item.field,
      type: col.filterType,
      value: {
        operator: basicOpTypeToWafflesOperatorMap.get(item.opCode),
        value: item.values[0],
      },
    };

    filterGroup.cols.push(newFilter);
    if (filterGroup.cols.length === 1) {
      wafflesFilters.push(filterGroup);
    }
  });

  return wafflesFilters;
};

export {
  APIFilterCriteria,
  ColumnFilter,
  genericFilter,
  mapOmniusCargoTypeToFilterType,
  //NumberQualifiers,
  //StringQualifiers,
  translateAPIFilterCriteriaToWafflesFilters,
  translateWafflesFiltersToApiFilterCriteria,
  wafflesDefaultFiltering,
  WafflesFilterLogic,
  WafflesFilterGroup,
  WafflesFilterType,
  wafflesNumberFilter,
  wafflesStringFilter,
};
